import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { FileText, MessageSquare, Sparkles, X } from "lucide-react"
import * as React from "react"
import { Link, useParams } from "react-router-dom"
import { toast } from "sonner"
import { applicationsApi } from "@/api/applications"
import { ApiError } from "@/api/client"
import { type CandidateApplication, candidatesApi } from "@/api/candidates"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { PageHeader } from "@/components/dashboard/page-header"
import { DetailPageSkeleton } from "@/components/shared/loading-skeletons"
import { formatDate } from "@/lib/utils"

export function CandidateDetailPage() {
  const { id = "" } = useParams()
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ["candidate", id],
    queryFn: () => candidatesApi.get(id),
    enabled: !!id,
  })

  const [notes, setNotes] = React.useState<string>("")
  const [tagInput, setTagInput] = React.useState("")
  React.useEffect(() => {
    if (q.data?.data.notes !== undefined) setNotes(q.data.data.notes ?? "")
  }, [q.data?.data.notes])

  const saveNotes = useMutation({
    mutationFn: () => candidatesApi.updateNotes(id, notes),
    onSuccess: () => {
      toast.success("Notlar kaydedildi.")
      qc.invalidateQueries({ queryKey: ["candidate", id] })
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Kaydedilemedi."),
  })

  const addTag = useMutation({
    mutationFn: (tag: string) => candidatesApi.addTag(id, tag),
    onSuccess: () => {
      setTagInput("")
      qc.invalidateQueries({ queryKey: ["candidate", id] })
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Etiket eklenemedi."),
  })

  const removeTag = useMutation({
    mutationFn: (tag: string) => candidatesApi.removeTag(id, tag),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["candidate", id] }),
  })

  if (q.isLoading) return <DetailPageSkeleton />
  if (!q.data) return <p className="p-6 text-muted-foreground">Aday bulunamadı.</p>
  const c = q.data.data

  return (
    <>
      <PageHeader title={c.full_name} description={c.email} />
      <div className="grid gap-4 p-4 sm:p-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardContent className="grid gap-3 p-6 text-sm md:grid-cols-2">
              <Field label="Telefon" value={c.phone || "—"} />
              <Field
                label="LinkedIn"
                value={
                  c.linkedin_url ? (
                    <a className="text-primary underline" href={c.linkedin_url} target="_blank" rel="noreferrer">
                      Profil
                    </a>
                  ) : (
                    "—"
                  )
                }
              />
              <Field label="Lokasyon" value={c.location || "—"} />
              <Field label="Kaynak" value={c.source || "—"} />
              <Field label="Eklenme" value={formatDate(c.created_at)} />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium">Notlar</h2>
                <Button
                  size="sm"
                  onClick={() => saveNotes.mutate()}
                  disabled={saveNotes.isPending || notes === (c.notes ?? "")}
                >
                  Kaydet
                </Button>
              </div>
              <Textarea
                rows={6}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Bu aday hakkındaki notlar…"
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 p-6">
              <h2 className="text-sm font-medium">Başvurular</h2>
              {c.applications.length === 0 ? (
                <p className="text-sm text-muted-foreground">Henüz başvuru yok.</p>
              ) : (
                <ul className="space-y-3 text-sm">
                  {c.applications.map((a) => (
                    <ApplicationItem key={a.id} app={a} candidateId={id} />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardContent className="space-y-3 p-6">
              <h2 className="text-sm font-medium">Etiketler</h2>
              <div className="flex flex-wrap gap-1">
                {c.tags.length === 0 && (
                  <span className="text-xs text-muted-foreground">Henüz etiket yok.</span>
                )}
                {c.tags.map((t) => (
                  <Badge key={t} variant="secondary" className="gap-1">
                    {t}
                    <button
                      type="button"
                      onClick={() => removeTag.mutate(t)}
                      className="rounded hover:bg-muted"
                      aria-label={`${t} etiketini sil`}
                    >
                      <X className="size-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <form
                className="flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault()
                  const t = tagInput.trim()
                  if (t) addTag.mutate(t)
                }}
              >
                <Input
                  placeholder="Yeni etiket"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                />
                <Button type="submit" variant="outline" disabled={!tagInput.trim() || addTag.isPending}>
                  Ekle
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  )
}

const SENIORITY_LABEL: Record<string, string> = {
  intern: "Stajyer",
  junior: "Junior",
  mid: "Mid",
  senior: "Senior",
  lead: "Lead",
  staff: "Staff",
  principal: "Principal",
}

function ApplicationItem({
  app: a,
  candidateId,
}: {
  app: CandidateApplication
  candidateId: string
}) {
  const qc = useQueryClient()
  const analyze = useMutation({
    mutationFn: () => applicationsApi.analyze(a.id),
    onSuccess: (res) => {
      toast.success(`Analiz tamam (skor: ${res.match_score ?? "—"}).`)
      qc.invalidateQueries({ queryKey: ["candidate", candidateId] })
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Analiz yapılamadı."),
  })
  const interview = useMutation({
    mutationFn: () => applicationsApi.interviewQuestions(a.id),
    onSuccess: () => {
      toast.success("Mülakat soruları üretildi.")
      qc.invalidateQueries({ queryKey: ["candidate", candidateId] })
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Sorular üretilemedi."),
  })

  return (
    <li className="space-y-2 border-b pb-3 last:border-0">
      <div className="flex items-center justify-between gap-2">
        <Link to={`/dashboard/jobs/${a.job_id}`} className="font-medium hover:underline">
          {a.job.title}
        </Link>
        <Badge
          variant={
            a.status === "hired"
              ? "success"
              : a.status === "rejected"
                ? "outline"
                : "secondary"
          }
        >
          {a.status}
        </Badge>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {a.stage && (
          <span className="inline-flex items-center gap-1">
            <span className="size-2 rounded-full" style={{ background: a.stage.color }} />
            {a.stage.name}
          </span>
        )}
        <span>·</span>
        <span>{formatDate(a.applied_at)}</span>
      </div>
      {a.resume && <ResumeLink applicationId={a.id} fileName={a.resume.file_name} />}

      <div className="flex items-center gap-2 pt-1">
        <Button
          size="sm"
          variant="outline"
          onClick={() => analyze.mutate()}
          disabled={analyze.isPending || !a.resume}
        >
          <Sparkles className="mr-1 size-3.5" />
          {analyze.isPending
            ? "Hirefy AI analiz ediyor…"
            : a.analysis
              ? "Yeniden analiz et"
              : "Hirefy AI analizi"}
        </Button>
        {a.analysis && !a.analysis.error && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => interview.mutate()}
            disabled={interview.isPending}
          >
            <MessageSquare className="mr-1 size-3.5" />
            {interview.isPending
              ? "Sorular üretiliyor…"
              : a.analysis.interview_questions
                ? "Soruları yenile"
                : "Mülakat soruları"}
          </Button>
        )}
      </div>

      {a.analysis && (
        <div className="mt-2 rounded-md border bg-muted/30 p-3 text-xs">
          {a.analysis.error ? (
            <p className="text-destructive">{a.analysis.error}</p>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                  <span>Skor: {a.analysis.match_score ?? "—"}/100</span>
                  {a.analysis.seniority_level && (
                    <Badge variant="outline" className="text-[10px]">
                      {SENIORITY_LABEL[a.analysis.seniority_level] ??
                        a.analysis.seniority_level}
                    </Badge>
                  )}
                  {a.analysis.experience_years !== null && (
                    <Badge variant="outline" className="text-[10px]">
                      {a.analysis.experience_years} yıl deneyim
                    </Badge>
                  )}
                </div>
                <span className="text-muted-foreground">
                  Hirefy AI · {formatDate(a.analysis.created_at)}
                </span>
              </div>
              {a.analysis.summary && <p>{a.analysis.summary}</p>}
              {a.analysis.strengths && a.analysis.strengths.length > 0 && (
                <div>
                  <div className="font-medium text-emerald-700">Güçlü yönler</div>
                  <ul className="list-disc space-y-0.5 pl-4">
                    {a.analysis.strengths.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}
              {a.analysis.weaknesses && a.analysis.weaknesses.length > 0 && (
                <div>
                  <div className="font-medium text-amber-700">Zayıf yönler</div>
                  <ul className="list-disc space-y-0.5 pl-4">
                    {a.analysis.weaknesses.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}
              {a.analysis.risk_factors && a.analysis.risk_factors.length > 0 && (
                <div>
                  <div className="font-medium text-rose-700">Risk faktörleri</div>
                  <ul className="list-disc space-y-0.5 pl-4">
                    {a.analysis.risk_factors.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}
              {a.analysis.skills_extracted && a.analysis.skills_extracted.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {a.analysis.skills_extracted.map((s) => (
                    <Badge key={s} variant="secondary" className="text-[10px]">
                      {s}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {a.cv_parsed && <CvSummaryCard cv={a.cv_parsed} />}

      {a.analysis?.interview_questions && (
        <div className="mt-2 rounded-md border bg-background p-3 text-xs">
          <div className="mb-2 text-sm font-semibold">Mülakat soruları</div>
          {a.analysis.interview_questions.technical.length > 0 && (
            <div className="mb-2">
              <div className="font-medium text-muted-foreground">Teknik</div>
              <ol className="list-decimal space-y-0.5 pl-4">
                {a.analysis.interview_questions.technical.map((q, i) => (
                  <li key={i}>{q}</li>
                ))}
              </ol>
            </div>
          )}
          {a.analysis.interview_questions.behavioral.length > 0 && (
            <div>
              <div className="font-medium text-muted-foreground">Davranışsal</div>
              <ol className="list-decimal space-y-0.5 pl-4">
                {a.analysis.interview_questions.behavioral.map((q, i) => (
                  <li key={i}>{q}</li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </li>
  )
}

function CvSummaryCard({ cv }: { cv: NonNullable<CandidateApplication["cv_parsed"]> }) {
  const hasContent =
    (cv.work_history?.length ?? 0) > 0 ||
    (cv.education?.length ?? 0) > 0 ||
    (cv.languages?.length ?? 0) > 0
  if (!hasContent) return null
  return (
    <div className="mt-2 rounded-md border bg-background p-3 text-xs">
      <div className="mb-2 text-sm font-semibold">CV özeti</div>
      {cv.work_history && cv.work_history.length > 0 && (
        <div className="mb-2">
          <div className="font-medium text-muted-foreground">Deneyim</div>
          <ul className="space-y-1">
            {cv.work_history.map((w, i) => (
              <li key={i}>
                <span className="font-medium">{w.role}</span>
                {" @ "}
                <span>{w.company}</span>
                <span className="text-muted-foreground">
                  {" · "}
                  {w.start_date ?? "?"} → {w.end_date ?? "devam"}
                </span>
                {w.summary && <div className="text-muted-foreground">{w.summary}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {cv.education && cv.education.length > 0 && (
        <div className="mb-2">
          <div className="font-medium text-muted-foreground">Eğitim</div>
          <ul className="space-y-0.5">
            {cv.education.map((e, i) => (
              <li key={i}>
                {e.degree} · {e.institution}
                {e.year ? ` · ${e.year}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
      {cv.languages && cv.languages.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {cv.languages.map((l) => (
            <Badge key={l} variant="outline" className="text-[10px]">
              {l}
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}

function ResumeLink({ applicationId, fileName }: { applicationId: string; fileName: string }) {
  const [loading, setLoading] = React.useState(false)
  async function open() {
    setLoading(true)
    try {
      const r = await applicationsApi.resumeUrl(applicationId)
      window.open(r.url, "_blank", "noopener,noreferrer")
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "CV açılamadı.")
    } finally {
      setLoading(false)
    }
  }
  return (
    <button
      type="button"
      onClick={open}
      disabled={loading}
      className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline disabled:opacity-50"
    >
      <FileText className="size-3.5" />
      {loading ? "Açılıyor…" : fileName}
    </button>
  )
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5">{value}</div>
    </div>
  )
}
